/**
 * financials-setup.ts — SIL-293 (W2, spec resources/plans/financials-hifi-flow-spec.md
 * slice 1 "Setup Guide"). Pure `stepStates(facts)` — the same seam as
 * `book-value.ts` (pure kernel beside the DTO; DB reads live in
 * `apps/api/src/lib/financials-setup.ts`, never here).
 *
 * Six steps, in fixed order (the array index + 1 IS the step number — never
 * reorder `SETUP_STEP_KEYS` without a coordinated UI change):
 *   1 connect_monitoring    — derived: active Datadog integration + telemetry coverage
 *   2 import_erp            — derived: finalized erp_register batch exists
 *   3 confirm_policy        — persisted: policy_confirmed_at; re-opens (spec
 *                             slice 1) when capitalization_policy.updated_at
 *                             > policy_confirmed_at — compared HERE, at read
 *                             time, from facts the caller already resolved;
 *                             no stored hash.
 *   4 review_book_vs_fmv    — persisted: book_vs_fmv_viewed_at (first-open event)
 *   5 model_scenario        — derived: org has >=1 saved scenario
 *   6 act_draft_listing     — derived: listing with source_scenario_id exists
 *
 * No DB access, no Date.now() — callers resolve every fact and pass plain
 * values (spec Cross-cutting "Assembly seam").
 */

import type { BasisEnvelope, CalculationBasisInput } from './dto/financials.js';

export const SETUP_STEP_KEYS = [
  'connect_monitoring',
  'import_erp',
  'confirm_policy',
  'review_book_vs_fmv',
  'model_scenario',
  'act_draft_listing',
] as const;
export type SetupStepKey = (typeof SETUP_STEP_KEYS)[number];

export const SETUP_TOTAL_STEPS = SETUP_STEP_KEYS.length;

/** `formulaId` for the readiness-meter basis (spec Cross-cutting "formulaId
 * ... defined beside the kernel it names") — defined here, beside
 * `stepStates`, per the epic's seam convention. */
export const SETUP_READINESS_FORMULA_ID = 'financials_setup.readiness.v1';

export interface SetupStepState {
  /** 1-based, matches `SETUP_STEP_KEYS` index + 1. */
  step: number;
  key: SetupStepKey;
  complete: boolean;
  /** Only meaningful for step 3 — true when a prior confirm has been
   * invalidated by a newer capitalization_policy write (spec: "re-opens
   * when capitalization_policy.updated_at > policy_confirmed_at"). Absent
   * (not merely `false`) on every other step — a step that was never
   * persisted has nothing to "re-open". */
  reopened?: boolean;
}

/**
 * All facts `stepStates` needs, resolved by
 * `apps/api/src/lib/financials-setup.ts::loadSetupFacts` under the caller's
 * `client_readonly` + GUC scope. Every field is the minimal derived/
 * persisted signal the spec table names for its step — no raw rows.
 */
export interface SetupFacts {
  /** Step 1a — an active (non-revoked) Datadog integration exists for the org. */
  datadogConnected: boolean;
  /** Step 1b — at least one asset has recent telemetry data (coverage > 0). */
  hasTelemetryCoverage: boolean;
  /** Step 2 — an `erp_register` import_batch reached `promoted` (spec:
   * "finalized" — mirrors erp-register.ts's `erp_register_finalize_batch`). */
  erpBatchFinalized: boolean;
  /** Step 3 — `financials_setup_state.policy_confirmed_at`, ISO string or null. */
  policyConfirmedAt: string | null;
  /** Step 3 staleness check — MAX(capitalization_policy.updated_at) across
   * every policy row (org-default + class overrides) for the org, ISO
   * string or null (null = no policy configured yet, step is simply
   * incomplete, not "reopened"). */
  policyUpdatedAt: string | null;
  /** Step 4 — `financials_setup_state.book_vs_fmv_viewed_at`, ISO string or null. */
  bookVsFmvViewedAt: string | null;
  /** Step 5 — count of saved `financial_scenario` rows for the org. */
  scenarioCount: number;
  /** Step 6 — at least one `listing` row has a non-null `source_scenario_id`. */
  listingFromScenarioExists: boolean;
}

/**
 * @pure
 */
export function stepStates(facts: SetupFacts): SetupStepState[] {
  const policyReopened =
    facts.policyConfirmedAt !== null &&
    facts.policyUpdatedAt !== null &&
    facts.policyUpdatedAt > facts.policyConfirmedAt;

  const policyConfirmComplete = facts.policyConfirmedAt !== null && !policyReopened;

  const step1: SetupStepState = {
    step: 1,
    key: 'connect_monitoring',
    complete: facts.datadogConnected && facts.hasTelemetryCoverage,
  };
  const step2: SetupStepState = {
    step: 2,
    key: 'import_erp',
    complete: facts.erpBatchFinalized,
  };
  const step3: SetupStepState = {
    step: 3,
    key: 'confirm_policy',
    complete: policyConfirmComplete,
    reopened: policyReopened,
  };
  const step4: SetupStepState = {
    step: 4,
    key: 'review_book_vs_fmv',
    complete: facts.bookVsFmvViewedAt !== null,
  };
  const step5: SetupStepState = {
    step: 5,
    key: 'model_scenario',
    complete: facts.scenarioCount >= 1,
  };
  const step6: SetupStepState = {
    step: 6,
    key: 'act_draft_listing',
    complete: facts.listingFromScenarioExists,
  };

  return [step1, step2, step3, step4, step5, step6];
}

// ---------------------------------------------------------------------------
// Readiness meter basis (spec: "Readiness meter is computed, never stored
// ... self-asserted ... must never appear in an export")
// ---------------------------------------------------------------------------

export interface SetupReadinessBasisInputs {
  pctAssetsWithFinancials: BasisEnvelope;
  pctLiveTelemetry: BasisEnvelope;
  compsFreshnessPct: BasisEnvelope;
}

/** Names match `SetupReadinessBasisInputs`' keys — asserted by the sibling
 * unit test, same "no type gymnastics" convention as `describeBookValueBasis`. */
export const SETUP_READINESS_BASIS_PARAM_NAMES = [
  'pctAssetsWithFinancials',
  'pctLiveTelemetry',
  'compsFreshnessPct',
] as const;

/**
 * Pure `describeX(inputs)` beside `stepStates`/the readiness formula (spec
 * Cross-cutting "Assembly seam"). `basisOf()` (dto/financials.ts) merges
 * these into the final `CalculationBasis` at the route.
 *
 * @pure
 */
export function describeSetupReadinessBasis(
  inputs: SetupReadinessBasisInputs,
): CalculationBasisInput[] {
  return (Object.keys(inputs) as Array<keyof SetupReadinessBasisInputs>).map((name) => ({
    name,
    ...inputs[name],
  }));
}

/**
 * Composite readiness score — simple average of the three 0-100 coverage
 * metrics (spec: "composite of existing coverage metrics"). No weighting
 * scheme is specified; equal-weight average is the least-surprising default
 * and is trivial to re-tune later since nothing is stored.
 *
 * @pure
 */
export function readinessScore(inputs: {
  pctAssetsWithFinancials: number;
  pctLiveTelemetry: number;
  compsFreshnessPct: number;
}): number {
  const avg =
    (inputs.pctAssetsWithFinancials + inputs.pctLiveTelemetry + inputs.compsFreshnessPct) / 3;
  return Math.round(avg);
}
